/**
 * Workspace-level roles, mirroring the API (ans-backend config/roles.ts):
 * owner 4, admin 3, planner 2, contractor 2, viewer 1. One role per person per
 * workspace; there is no per-project role.
 *
 * The hierarchy is linear and is NOT the money boundary: a contractor has
 * planner's level (full operational access) but never sees money. Money
 * screens must depend on isFinanciallyBlind(), not on a role level.
 */
import type { UserRole } from '../types/User';

export const ROLE_LEVEL: Readonly<Record<UserRole, number>> = {
  owner: 4,
  admin: 3,
  planner: 2,
  contractor: 2,
  viewer: 1,
};

/** Plain wording for role pickers and badges. */
export const ROLE_LABELS: Readonly<Record<UserRole, string>> = {
  owner: 'Owner',
  admin: 'Admin',
  planner: 'Planner',
  contractor: 'Contractor',
  viewer: 'Viewer',
};

/** Numeric level; 0 for an absent or unrecognised role (which passes no gate). */
export function roleLevel(role: string | null | undefined): number {
  if (typeof role !== 'string') return 0;
  return Object.prototype.hasOwnProperty.call(ROLE_LEVEL, role) ? ROLE_LEVEL[role as UserRole] : 0;
}

export function isKnownRole(role: string | null | undefined): role is UserRole {
  return roleLevel(role) > 0;
}

/** Every known role except contractor may see money; an unknown role may not. */
export function canRoleViewFinancials(role: string | null | undefined): boolean {
  return isKnownRole(role) && role !== 'contractor';
}

/**
 * Blind when flagged OR the role may not see money. The flag can only add
 * blindness, never remove it (same rule as the API).
 */
export function isFinanciallyBlind(role: string | null | undefined, flag?: boolean | null): boolean {
  return flag === true || !canRoleViewFinancials(role);
}

/** Roles in the order pickers list them, highest first. */
export const ROLE_ORDER: readonly UserRole[] = ['owner', 'admin', 'planner', 'contractor', 'viewer'];

/** One plain line per role, shown beside every role picker. */
export const ROLE_DESCRIPTIONS: Readonly<Record<UserRole, string>> = {
  owner: 'Full access: manage people, settings and all data',
  admin: 'Manage people, settings and all data, short of owner-only actions',
  planner: 'Can create and edit: events, tasks, vendors, guests, money',
  contractor: 'Can create and edit operational data; never sees money',
  viewer: 'Read-only access across all sections',
};

/** Badge colour class per role. */
export const ROLE_BADGE_CLASS: Readonly<Record<UserRole, string>> = {
  owner: 'ds-badge-danger',
  admin: 'ds-badge-danger',
  planner: 'ds-badge-warning',
  contractor: 'ds-badge-warning',
  viewer: 'ds-badge-neutral',
};
